import React, { useState } from 'react';
import { DillButton, DillModal } from '../../../general/Dill';
import ansoniaLogo from '../../../../assets/images/ansonia.png';
import creditsafeLogo from '../../../../assets/images/creditsafe.png';
import dandbLogo from '../../../../assets/images/dandb.jpg';
import equifaxLogo from '../../../../assets/images/equifax.png';
import experianLogo from '../../../../assets/images/experian.png';
import lexisnexisLogo from '../../../../assets/images/lexisnexis.png';
import nacmLogo from '../../../../assets/svg/nacm.svg';
import moodysLogo from '../../../../assets/images/moodys.jpg';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { openDillToast } from '../../../../utils/helpers';
import {
  creditSafeCompanySearch,
  // directorSearch,
  getNACMCompanyLookupList,
  getExperianBusinessSearch,
} from '../../../../redux/services/creditReportService';
import { sendCreditReportNotification } from '../../../../redux/services/creditApplicationsService';
import { AppDropDown } from '../../../general/AppDropDown/AppDropDown';

const report = [
  {
    id: 'ansonia',
    name: 'Ansonia',
    subtitle: 'Specialized Bureau',
    description: 'Specialized trade credit data from Ansonia Data',
    logo: ansoniaLogo,
  },
  {
    id: 'creditsafe',
    name: 'CreditSafe',
    subtitle: 'Finance Health',
    description: 'Assessing corporate reliability and financials',
    logo: creditsafeLogo,
  },
  {
    id: 'dandb',
    name: 'Dun & Bradstreet',
    subtitle: 'Risk Management',
    description: 'Mitigating risk with reliable data insights',
    logo: dandbLogo,
  },
  {
    id: 'equifax',
    name: 'Equifax',
    subtitle: 'Data Analytics',
    description: 'Transforming data into actionable insights',
    logo: equifaxLogo,
  },
  {
    id: 'experian',
    name: 'Experian',
    subtitle: 'Business Credit Reports',
    description: 'In-depth analysis of business credit profiles',
    logo: experianLogo,
  },
  {
    id: 'lexisnexis',
    name: 'LexisNexis',
    subtitle: 'Enterprise',
    description: 'Extensive business and risk data integration from LexisNexis',
    logo: lexisnexisLogo,
  },
  {
    id: 'nacm',
    name: 'NACM',
    subtitle: 'Specialized Bureau',
    description: 'Integration with NACM trade credit reports',
    logo: nacmLogo,
  },
  {
    id: 'moodys',
    name: `Cortera/Moody\'s`,
    subtitle: 'Credit Ratings',
    description: 'Expert analysis and rating for investment decisions',
    logo: moodysLogo,
  },
];

const CreditReportModal = ({
  open,
  handleClose,
  companyDetails,
  onLearnMore,
  creditApplicationId,
  internalFormSubmissionId,
  guarantors,
  overlayZIndex,
}: {
  open: boolean;
  creditApplicationId: string;
  // On an internal form's review page (used to label a report request email).
  internalFormSubmissionId?: string;
  handleClose: () => void;
  overlayZIndex?: number;
  companyDetails: {
    companyName: string;
    address?: string;
    city?: string;
    state?: string;
    telephone?: string;
    zipCode?: string;
  };
  onLearnMore: (
    data: {
      id: string;
      name: string;
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      zipCode: string;
      telephone: string;
      confidence: string | number | null;
      tradelines: string | number | null;
      status?: string;
      safeNo?: string;
      regNo?: string;
      id2?: string;
    }[],
    error: string,
    type: string,
    tab: 'business' | 'personal',
    pageSize?: number
  ) => void;
  guarantors: { name: string }[];
}) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState('');
  const [tab, setTab] = useState<'business' | 'personal'>('business');
  const [selectedGuarantor, setSelectedGuarantor] = useState('');
  const [selectedGurantorError, setSelectedGurantorError] = useState('');

  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const isAdmin = Boolean(user?.roles?.includes('DILL_ADMIN'));
  const creditReportSettings = currentSupplier?.creditReportSettings;

  // A provider only counts as "configured" once its credentials are actually set.
  const isProviderConfigured: Record<string, boolean> = {
    nacm: Boolean(
      creditReportSettings?.nacmCreditReportUrl && creditReportSettings?.nacmCreditReportToken
    ),
    creditsafe: Boolean(
      creditReportSettings?.creditSafeUsername && creditReportSettings?.creditSafePassword
    ),
    experian: Boolean(
      creditReportSettings?.experianUsername &&
        creditReportSettings?.experianPassword &&
        creditReportSettings?.experianClientId &&
        creditReportSettings?.experianClientSecret
    ),
  };

  // Admins can see every provider (configured or not); everyone else only sees
  // nacm/creditsafe/experian once the supplier has credentials set up for it.
  const visibleReports = report.filter((item) => isAdmin || isProviderConfigured[item.id]);

  const handleCloseModal = () => {
    setSelectedGuarantor('');
    setSelectedGurantorError('');
    setLoading('');
    setTab('business');
    handleClose();
  };

  const handleLearnMore = async (val: string, name: string) => {
    const normalizedVal = val.toLowerCase();
    if (tab === 'personal') {
      if (selectedGuarantor === '') {
        setSelectedGurantorError('Please select a guarantor');
        return;
      }
    }
    setLoading(val);

    switch (normalizedVal) {
      case 'nacm': {
        if (tab === 'personal') {
          openDillToast({
            message: 'NACM Report not available for personal report.',
            type: 'ERROR',
          });

          setLoading('');
          return;
        }
        const result = await dispatch(
          getNACMCompanyLookupList({
            supplierId: currentSupplier?.id ?? '',
            companyDetails: companyDetails,
          })
        );

        if (result.type === 'creditReport/getNACMCompanyLookupList/fulfilled') {
          const payload: any = result.payload;
          const data = payload?.data.map((item: any) => ({
            id: item.companyNACMID,
            name: item.name,
            addressLine1: item.addressLine1,
            addressLine2: item.addressLine2,
            city: item.city,
            state: item.state,
            zipCode: item.zipCode,
            telephone: item.telephone,
            confidence: item.confidence,
            tradelines: item.tradelines,
          }));
          onLearnMore(data, '', 'nacm', tab);
        } else {
          const payload: any = result.payload;
          const errorText = typeof payload === 'string' ? payload : 'Failed to get credit report.';

          onLearnMore(
            [] as {
              id: string;
              name: string;
              addressLine1: string;
              addressLine2: string;
              city: string;
              state: string;
              zipCode: string;
              telephone: string;
              confidence: string | number | null;
              tradelines: string | number | null;
            }[],
            errorText,
            'nacm',
            tab
          );
        }
        break;
      }

      case 'creditsafe': {
        if (tab === 'personal') {
          openDillToast({
            message: 'CreditSafe Report not available for personal report.',
            type: 'ERROR',
          });

          setLoading('');
          return;

          // const splitName = selectedGuarantor.split(' ');
          // const result = await dispatch(
          //   directorSearch({
          //     supplierId: currentSupplier?.id ?? '',
          //     companyDetails: {
          //       firstName: splitName[0],
          //       lastName: splitName[1],
          //     },
          //   })
          // );

          // if (result.type.includes('fulfilled')) {
          //   const payload: any = result.payload;

          //   const data = payload?.data?.directors.map((item: any) => ({
          //     id: item.peopleId,
          //     name: item.firstName + ' ' + item.lastName,
          //     addressLine1: item.address.street,
          //     addressLine2: '',
          //     city: item.address.city,
          //     state: item.address.province,
          //     zipCode: item.address.postCode,
          //     telephone: '',
          //     confidence: null,
          //     tradelines: null,
          //     status: item.title,
          //   }));
          //   onLearnMore(
          //     data,
          //     '',
          //     'creditsafe',
          //     tab,
          //     payload?.data?.totalSize ? Number(payload?.data?.totalSize) : 1
          //   );
          // } else {
          //   const payload: any = result.payload;
          //   const errorText =
          //     typeof payload === 'string' ? payload : 'Failed to get credit report.';

          //   onLearnMore(
          //     [] as {
          //       id: string;
          //       name: string;
          //       addressLine1: string;
          //       addressLine2: string;
          //       city: string;
          //       state: string;
          //       zipCode: string;
          //       telephone: string;
          //       confidence: string | number | null;
          //       tradelines: string | number | null;
          //     }[],
          //     errorText,
          //     'creditsafe',
          //     tab
          //   );
          // }

          // setLoading('');
          // return;
        }
        const result = await dispatch(
          creditSafeCompanySearch({
            supplierId: currentSupplier?.id ?? '',
            companyDetails: companyDetails,
          })
        );

        if (result.type === 'creditReport/creditSafeCompanySearch/fulfilled') {
          const payload: any = result.payload;
          const data = payload?.data?.companies.map((item: any) => ({
            id: item.id,
            name: item.name,
            addressLine1: item.address.street,
            addressLine2: '',
            city: item.address.city,
            state: item.address.province,
            zipCode: item.address.postCode,
            telephone: '',
            confidence: null,
            tradelines: null,
            status: item.status,
            safeNo: item.safeNo,
            regNo: item.regNo,
            id2: item.id2,
          }));
          onLearnMore(
            data,
            '',
            'creditsafe',
            tab,
            payload?.data?.totalSize ? Number(payload?.data?.totalSize) : 1
          );
        } else {
          const payload: any = result.payload;
          const errorText = typeof payload === 'string' ? payload : 'Failed to get credit report.';

          onLearnMore(
            [] as {
              id: string;
              name: string;
              addressLine1: string;
              addressLine2: string;
              city: string;
              state: string;
              zipCode: string;
              telephone: string;
              confidence: string | number | null;
              tradelines: string | number | null;
            }[],
            errorText,
            'creditsafe',
            tab
          );
        }
        break;
      }

      case 'experian': {
        if (tab === 'personal') {
          openDillToast({
            message: 'Experian Report not available for personal report.',
            type: 'ERROR',
          });
          setLoading('');
          return;
        }
        const experianResult = await dispatch(
          getExperianBusinessSearch({
            supplierId: currentSupplier?.id ?? '',
            companyDetails: {
              companyName: companyDetails.companyName,
              city: companyDetails.city,
              state: companyDetails.state,
              zip: companyDetails.zipCode,
            },
          })
        );
        if (experianResult.type === 'creditReport/getExperianBusinessSearch/fulfilled') {
          const payload: any = experianResult.payload;
          const data = payload?.data.map((item: any) => ({
            id: item.experianBin,
            name: item.name,
            addressLine1: item.addressLine1 ?? '',
            addressLine2: item.addressLine2 ?? '',
            city: item.city ?? '',
            state: item.state ?? '',
            zipCode: item.zipCode ?? '',
            telephone: item.phone ?? '',
            confidence: item.confidence ?? null,
            tradelines: item.tradelines ?? null,
          }));
          onLearnMore(data, '', 'experian', tab);
        } else {
          const payload: any = experianResult.payload;
          const errorText =
            typeof payload === 'string' ? payload : 'Failed to get Experian report.';
          onLearnMore(
            [] as {
              id: string;
              name: string;
              addressLine1: string;
              addressLine2: string;
              city: string;
              state: string;
              zipCode: string;
              telephone: string;
              confidence: string | number | null;
              tradelines: string | number | null;
            }[],
            errorText,
            'experian',
            tab
          );
        }
        break;
      }

      // reserved for future providers
      case 'equifax':
      case 'ansonia':
      case 'dandb':
      case 'lexisnexis':
      case 'moodys':
        const result = await dispatch(
          sendCreditReportNotification({
            supplierId: currentSupplier?.id ?? '',
            sender: currentSupplier?.email ?? '',
            creditApplicationId: creditApplicationId || internalFormSubmissionId || '',
            message: val,
          })
        );
        if (result.type === 'creditApplications/sendCreditReportNotification/fulfilled') {
          openDillToast({
            message: 'Credit report request sent successfully to Dill team.',
            type: 'SUCCESS',
          });
        } else {
          openDillToast({
            message: 'Failed to send credit report request.',
            type: 'ERROR',
          });
        }
        break;

      default:
        openDillToast({
          message: 'Unknown provider selected.',
          type: 'ERROR',
          autoClose: 3000,
        });
        break;
    }

    setLoading('');
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Credit Report"
      maxWidth="60vw"
      overlayZIndex={overlayZIndex}
      footer={
        <div className="flex justify-end w-full">
          <DillButton label="Close" variant="secondary" onClick={handleCloseModal} />
        </div>
      }>
      <div className="flex flex-col">
        <div className="flex flex-row w-full border-b sticky top-0 bg-white z-10">
          <div
            onClick={() => setTab('business')}
            className={`flex p-4 flex-row w-full cursor-pointer border-b-[4px] ${
              tab === 'business'
                ? 'border-blue-500 PRIMARY_100-BG PRIMARY_500-CLR'
                : 'GREY_200-BORDER hover:text-blue-500 hover:bg-blue-50 GREY_50-BG'
            }`}>
            Business
          </div>
          <div
            onClick={() => setTab('personal')}
            className={`flex p-4 flex-row w-full cursor-pointer border-b-[4px] ${
              tab === 'personal'
                ? 'border-blue-500 PRIMARY_100-BG PRIMARY_500-CLR'
                : 'GREY_200-BORDER hover:text-blue-500 hover:bg-blue-50 GREY_50-BG'
            }`}>
            Personal
          </div>
        </div>
        <div className="py-4 flex flex-col w-full">
          {tab === 'personal' && (
            <div className="w-full mb-4">
              <AppDropDown
                label="Guarantor"
                isRequired={true}
                placeholder="Select guarantor"
                errorText={selectedGurantorError}
                value={selectedGuarantor}
                items={guarantors.map((value) => ({ label: value.name, value: value.name }))}
                onSelectChange={(item) => {
                  setSelectedGuarantor(item?.value ?? '');
                  setSelectedGurantorError('');
                }}
              />
            </div>
          )}
          {visibleReports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <p className="text-sm font-semibold GREY_700-CLR">
                No credit report settings configured
              </p>
              <p className="text-xs GREY_500-CLR mt-1">
                Contact your admin to set up NACM, CreditSafe, or Experian credentials for this
                account.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {visibleReports.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-3 border rounded-lg flex-col">
                  <div className="flex gap-2 items-center">
                    <img src={item.logo} alt={item.name} className="w-10 h-10 object-contain" />
                    <div className="flex flex-col">
                      <p className="font-semibold text-sm">{item.name}</p>
                      <p className="text-xs GREY_500-CLR mt-1 font-medium">{item.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-xs GREY_500-CLR mb-1 font-medium">{item.description}</p>
                  <DillButton
                    label="Learn More"
                    variant="secondary"
                    onClick={() => handleLearnMore(item.id, item.name)}
                    isLoading={loading === item.id}
                    isDisabled={loading !== ''}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DillModal>
  );
};

export default CreditReportModal;
